<script lang="ts" setup>
import { useBuilderStore, type WorkflowBuilderJourneyEventType } from '../../builder.store';
import { useUsersStore } from '@/features/settings/users/users.store';
import { useWorkflowHistoryStore } from '@/features/workflows/workflowHistory/workflowHistory.store';
import { useHistoryStore } from '@/app/stores/history.store';
import { useCollaborationStore } from '@/features/collaboration/collaboration/collaboration.store';
import { useWorkflowSaveStore } from '@/app/stores/workflowSave.store';
import { AutoSaveState, VIEWS } from '@/app/constants';
import { computed, watch, ref, nextTick, useSlots } from 'vue';
import { useInjectWorkflowId } from '@/app/composables/useInjectWorkflowId';
import { useTelemetry } from '@/app/composables/useTelemetry';
import { useI18n } from '@n8n/i18n';
import { useWorkflowsStore } from '@/app/stores/workflows.store';
import {
	useWorkflowDocumentStore,
	createWorkflowDocumentId,
} from '@/app/stores/workflowDocument.store';
import { useRoute, useRouter } from 'vue-router';
import type {
	ChatUI,
	RatingFeedback,
	WorkflowSuggestion,
} from '@n8n/design-system/types/assistant';
import { isTaskAbortedMessage, isWorkflowUpdatedMessage } from '@n8n/design-system/types/assistant';
import { nodeViewEventBus } from '@/app/event-bus';
import { jsonParse } from 'n8n-workflow';
import ExecuteMessage from './ExecuteMessage.vue';
import NotificationPermissionBanner from './NotificationPermissionBanner.vue';
import ChatVersionCard from './ChatVersionCard.vue';
import ReviewChangesBanner from './ReviewChangesBanner.vue';
import CreditsSettingsDropdown from './CreditsSettingsDropdown.vue';
import CreditWarningBanner from './CreditWarningBanner.vue';
import ChatInputWithMention from '../FocusedNodes/ChatInputWithMention.vue';
import MessageFocusedNodesChips from '../FocusedNodes/MessageFocusedNodesChips.vue';
import { useRunWorkflow } from '@/app/composables/useRunWorkflow';
import { useNodeTypesStore } from '@/app/stores/nodeTypes.store';
import { isChatNode } from '@/app/utils/aiUtils';
import { useLogsStore } from '@/app/stores/logs.store';
import { usePageRedirectionHelper } from '@/app/composables/usePageRedirectionHelper';
import { useBrowserNotifications } from '@/app/composables/useBrowserNotifications';
import { useToast } from '@/app/composables/useToast';
import { useDocumentVisibility } from '@/app/composables/useDocumentVisibility';
import { WORKFLOW_SUGGESTIONS } from '@/app/constants/workflowSuggestions';
import { useWorkflowUpdate } from '@/app/composables/useWorkflowUpdate';
import { canvasEventBus } from '@/features/workflows/canvas/canvas.eventBus';
import { useErrorHandler } from '@/app/composables/useErrorHandler';
import type { WorkflowDataUpdate } from '@n8n/rest-api-client/api/workflows';
import shuffle from 'lodash/shuffle';
import { useAssistantStore } from '@/features/ai/assistant/assistant.store';
import { useSettingsStore } from '@/app/stores/settings.store';
import { useChatPanelStateStore } from '@/features/ai/assistant/chatPanelState.store';
import { useReviewChanges } from '@/features/ai/assistant/composables/useReviewChanges';
import { watchExecutionCompletion } from '@/features/ai/assistant/composables/useExecutionWatcher';

import { N8nAskAssistantChat, N8nInfoTip } from '@n8n/design-system';
import BuildModeEmptyState from './BuildModeEmptyState.vue';
import {
	isPlanModePlanMessage,
	isPlanModeQuestionsMessage,
	isPlanModeUserAnswersMessage,
	isVersionCardMessage,
	isWebFetchApprovalCustomMessage,
	isCollapsedGroupMessage,
	createCollapsedGroupMessage,
	type PlanMode,
} from '../../assistant.types';
import CollapsedMessagesGroup from './CollapsedMessagesGroup.vue';
import PlanDisplayMessage from './PlanDisplayMessage.vue';
import PlanModeSelector from './PlanModeSelector.vue';
import PlanQuestionsMessage from './PlanQuestionsMessage.vue';
import UserAnswersMessage from './UserAnswersMessage.vue';
import WebFetchApprovalMessage from './WebFetchApprovalMessage.vue';

const emit = defineEmits<{
	close: [];
}>();

const builderStore = useBuilderStore();
const usersStore = useUsersStore();
const workflowHistoryStore = useWorkflowHistoryStore();
const historyStore = useHistoryStore();
const collaborationStore = useCollaborationStore();
const workflowAutosaveStore = useWorkflowSaveStore();
const workflowId = useInjectWorkflowId();
const telemetry = useTelemetry();
const slots = useSlots();
const workflowsStore = useWorkflowsStore();
const workflowDocumentStore = computed(() =>
	workflowId.value
		? useWorkflowDocumentStore(createWorkflowDocumentId(workflowId.value))
		: undefined,
);
const assistantStore = useAssistantStore();
const settingsStore = useSettingsStore();
const chatPanelStateStore = useChatPanelStateStore();
const router = useRouter();
const i18n = useI18n();
const route = useRoute();
const { goToUpgrade } = usePageRedirectionHelper();
const toast = useToast();
const { runWorkflow } = useRunWorkflow({ router });
const nodeTypesStore = useNodeTypesStore();
const logsStore = useLogsStore();
const { updateWorkflow } = useWorkflowUpdate();
const { handleError } = useErrorHandler({
	source: 'ai-builder',
	titleKey: 'aiAssistant.builder.error.title',
});
const { onDocumentVisible } = useDocumentVisibility();
const { canPrompt } = useBrowserNotifications();

onDocumentVisible(() => {
	builderStore.clearDoneIndicatorTitle();
});

// Track processed workflow updates and accumulated node IDs for tidyUp
const processedWorkflowUpdates = ref(new Set<string>());
const accumulatedNodeIdsToTidyUp = ref<string[]>([]);
const n8nChatRef = ref<InstanceType<typeof N8nAskAssistantChat>>();
const chatInputRef = ref<InstanceType<typeof ChatInputWithMention>>();
const suggestionsInputRef = ref<InstanceType<typeof ChatInputWithMention>>();
const inputText = ref('');

const creditBannerDismissed = ref(false);

watch(
	() => builderStore.creditsRemaining,
	() => {
		creditBannerDismissed.value = false;
	},
);

const showCreditBanner = computed(() => {
	return builderStore.isLowCredits && !creditBannerDismissed.value;
});

const showUsabilityNotice = computed(
	() =>
		assistantStore.canManageAISettings && !settingsStore.settings.ai.allowSendingParameterValues,
);

const shouldShowNotificationBanner = computed(() => {
	return builderStore.streaming && canPrompt.value;
});

watch(shouldShowNotificationBanner, (isShown) => {
	if (isShown) {
		builderStore.trackWorkflowBuilderJourney('browser_notification_ask_permission');
	}
});

const user = computed(() => ({
	firstName: usersStore.currentUser?.firstName ?? '',
	lastName: usersStore.currentUser?.lastName ?? '',
}));

const loadingMessage = computed(() => {
	// Check if we have any running tool messages visible in the chat
	const hasVisibleRunningTools = builderStore.getRunningTools(builderStore.chatMessages).length > 0;
	// Don't show loading message if tools are already visible and running
	// to avoid duplicate display (tool messages show their own status)
	if (hasVisibleRunningTools) {
		return undefined;
	}

	return builderStore.builderThinkingMessage;
});
const currentRoute = computed(() => route.name);
const showExecuteMessage = computed(() => {
	const messages = builderStore.chatMessages;
	const builderUpdatedWorkflowMessageIndex = messages.findLastIndex(
		(msg) =>
			msg.type === 'workflow-updated' ||
			(msg.type === 'tool' && msg.toolName === 'update_node_parameters'),
	);

	// Check if there's an error message or task aborted message after the last workflow update
	const messagesAfterUpdate = messages.slice(builderUpdatedWorkflowMessageIndex + 1);
	const hasErrorAfterUpdate = messagesAfterUpdate.some((msg) => msg.type === 'error');
	const hasTaskAbortedAfterUpdate = messagesAfterUpdate.some((msg) => isTaskAbortedMessage(msg));

	// Hide when the last assistant message is a question or plan awaiting user action
	const lastAssistantMessage = messages.findLast((msg) => msg.role === 'assistant');
	const hasPendingInteraction =
		lastAssistantMessage &&
		(isPlanModeQuestionsMessage(lastAssistantMessage) ||
			isPlanModePlanMessage(lastAssistantMessage) ||
			isWebFetchApprovalCustomMessage(lastAssistantMessage));

	return (
		!builderStore.streaming &&
		(workflowDocumentStore.value?.allNodes ?? []).length > 0 &&
		builderStore.hasMessages &&
		!hasErrorAfterUpdate &&
		!hasTaskAbortedAfterUpdate &&
		!hasPendingInteraction
	);
});
const creditsQuota = computed(() => builderStore.creditsQuota);
const creditsRemaining = computed(() => builderStore.creditsRemaining);
const showAskOwnerTooltip = computed(() => !usersStore.isInstanceOwner);

// Use different completion message for code-builder
const thinkingCompletionMessage = computed(() =>
	builderStore.isCodeBuilder
		? i18n.baseText('aiAssistant.builder.thinkingCompletionMessage.codeBuilder')
		: undefined,
);

const workflowSuggestions = computed<WorkflowSuggestion[] | undefined>(() => {
	if (builderStore.hasMessages || (workflowDocumentStore.value?.allNodes ?? []).length > 0) {
		return undefined;
	}
	return shuffle(WORKFLOW_SUGGESTIONS);
});

const isAutosaving = computed(() => {
	return (
		workflowAutosaveStore.autoSaveState === AutoSaveState.Scheduled ||
		workflowAutosaveStore.autoSaveState === AutoSaveState.InProgress
	);
});

const isInputDisabled = computed(() => {
	return collaborationStore.shouldBeReadOnly || isAutosaving.value;
});

const isChatInputDisabled = computed(() => {
	return isInputDisabled.value || builderStore.shouldDisableChatInput;
});

const {
	versionNodeChangesMap,
	showReviewChanges,
	nodeChanges,
	isExpanded,
	toggleExpanded,
	openDiffView,
} = useReviewChanges();

function onSelectChangedNode(nodeId: string) {
	canvasEventBus.emit('nodes:select', { ids: [nodeId], panIntoView: true });
}

/** Returns true if the version card has at least one node change */
function isNonEmptyVersionCard(message: ChatUI.AssistantMessage): boolean {
	if (!isVersionCardMessage(message)) return false;
	return (versionNodeChangesMap.value.get(message.data.versionId) ?? []).length > 0;
}

function isCurrentVersionCard(message: ChatUI.AssistantMessage): boolean {
	if (!isNonEmptyVersionCard(message)) return false;
	// If restore happened but no new messages sent yet, the restored card is current
	if (builderStore.activeVersionCardId && !builderStore.resumeAfterRestoreMessageId) {
		return message.id === builderStore.activeVersionCardId;
	}
	// Default (including after resume): last non-empty, non-collapsed version card
	const collapsed = builderStore.collapsedMessageIds;
	const nonEmptyVisibleCards = builderStore.chatMessages.filter(
		(msg) =>
			isVersionCardMessage(msg) &&
			isNonEmptyVersionCard(msg) &&
			(!msg.id || !collapsed.has(msg.id)),
	);
	return nonEmptyVisibleCards[nonEmptyVisibleCards.length - 1]?.id === message.id;
}

function getVersionIndex(message: ChatUI.AssistantMessage): number {
	let count = 0;
	for (const msg of builderStore.chatMessages) {
		if (isVersionCardMessage(msg) && isNonEmptyVersionCard(msg)) {
			count++;
			if (msg.id === message.id) return count;
		}
	}
	return count + 1;
}

/** Messages with collapsed groups substituted for collapsed message ranges */
const displayMessages = computed(() => {
	const collapsed = builderStore.collapsedMessageIds;
	if (!collapsed.size) return builderStore.chatMessages;

	const result: ChatUI.AssistantMessage[] = [];
	const group: ChatUI.AssistantMessage[] = [];

	for (const msg of builderStore.chatMessages) {
		if (msg.id && collapsed.has(msg.id)) {
			group.push(msg);
		} else {
			if (group.length > 0) {
				result.push(createCollapsedGroupMessage([...group]));
				group.length = 0;
			}
			result.push(msg);
		}
	}
	if (group.length > 0) {
		result.push(createCollapsedGroupMessage(group));
	}
	return result;
});

async function onCodeReplace(index: number) {
	await builderStore.applyCodeDiff(workflowId.value, index);
}

async function onCodeUndo(index: number) {
	await builderStore.undoCodeDiff(workflowId.value, index);
}

const disabledTooltip = computed(() => {
	if (!isChatInputDisabled.value) {
		return undefined;
	}
	if (isAutosaving.value) {
		return i18n.baseText('aiAssistant.builder.disabledTooltip.autosaving');
	}
	if (collaborationStore.shouldBeReadOnly) {
		return i18n.baseText('aiAssistant.builder.disabledTooltip.readOnly');
	}
	return undefined;
});

/**
 * Check if questions have been answered (there's a user_answers message after this questions message)
 */
function isQuestionsAnswered(questionsMessage: { id?: string }): boolean {
	const messages = builderStore.chatMessages;
	const questionsIndex = messages.findIndex((m) => m.id === questionsMessage.id);
	if (questionsIndex === -1) return false;

	for (let i = questionsIndex + 1; i < messages.length; i++) {
		if (isPlanModeUserAnswersMessage(messages[i])) {
			return true;
		}
	}
	return false;
}

/**
 * The last unanswered questions message (if any) — to render in the input slot.
 */
const activeQuestionsMessage = computed(() => {
	const messages = builderStore.chatMessages;
	const lastQuestions = messages.findLast((m) => isPlanModeQuestionsMessage(m));
	if (!lastQuestions || !isPlanModeQuestionsMessage(lastQuestions)) return undefined;
	if (isQuestionsAnswered(lastQuestions)) return undefined;
	return lastQuestions;
});

/**
 * Check if this plan message is the last one and nothing has been sent after it.
 * Only the latest plan with no subsequent messages should show the "Implement" button,
 * because the HITL interrupt only works for the pending plan.
 */
function isLastPlanMessage(message: PlanMode.PlanMessage): boolean {
	const messages = builderStore.chatMessages;
	const idx = messages.findLastIndex((m) => m.id === message.id);
	if (idx === -1) return false;
	// Check that no user message or other content exists after this plan
	return !messages.slice(idx + 1).some((m) => m.role === 'user');
}

async function onWebFetchDecision(payload: {
	requestId: string;
	url: string;
	domain: string;
	action: 'allow_once' | 'allow_domain' | 'allow_all' | 'deny';
}) {
	builderStore.trackWorkflowBuilderJourney('web_fetch_decision', {
		domain: payload.domain,
		url: payload.url,
		decision: payload.action,
	});

	const textMap: Record<string, string> = {
		deny: i18n.baseText('aiAssistant.builder.webFetch.deny'),
		allow_once: i18n.baseText('aiAssistant.builder.webFetch.allowOnce'),
		allow_domain: i18n.baseText('aiAssistant.builder.webFetch.allowDomain', {
			interpolate: { domain: payload.domain },
		}),
		allow_all: i18n.baseText('aiAssistant.builder.webFetch.allowAll'),
	};

	await builderStore.sendChatMessage({
		text: textMap[payload.action],
		resumeData: payload,
		skipUserMessage: true,
	});
}

async function onUserMessage(content: string) {
	// Record activity to maintain write lock while building
	collaborationStore.requestWriteAccess();

	// Reset accumulated node IDs for each new message exchange
	accumulatedNodeIdsToTidyUp.value = [];

	// If the workflow is empty, set the initial generation flag
	const isInitialGeneration = (workflowDocumentStore.value?.allNodes ?? []).length === 0;

	await builderStore.sendChatMessage({
		text: content,
		initialGeneration: isInitialGeneration,
	});
}

async function onCustomInputSubmit() {
	if (!inputText.value.trim()) return;
	const content = inputText.value;
	inputText.value = '';
	await onUserMessage(content);
}

let mockDataExecutionWatcherStop: (() => void) | undefined;

function onNewWorkflow() {
	builderStore.resetBuilderChat();
	processedWorkflowUpdates.value.clear();
	accumulatedNodeIdsToTidyUp.value = [];
	mockDataExecutionWatcherStop?.();
	mockDataExecutionWatcherStop = undefined;
}

function onFeedback(feedback: RatingFeedback) {
	if (feedback.rating) {
		telemetry.track('User rated workflow generation', {
			helpful: feedback.rating === 'up',
			workflow_id: workflowId.value,
			session_id: builderStore.trackingSessionId,
		});
	}
	if (feedback.feedback) {
		telemetry.track('User submitted workflow generation feedback', {
			feedback: feedback.feedback,
			workflow_id: workflowId.value,
			session_id: builderStore.trackingSessionId,
			user_message_id: builderStore.lastUserMessageId,
		});
	}
}

async function onWorkflowExecuted() {
	// The wizard executes individual nodes, not the full workflow,
	// so there's no full execution data to inspect — just send success.
	if (builderStore.wizardHasExecutedWorkflow) {
		await builderStore.sendChatMessage({
			text: i18n.baseText('aiAssistant.builder.executeMessage.wizardSetupSuccess'),
			type: 'execution',
			executionStatus: 'success',
		});
		return;
	}

	const executionData = workflowsStore.workflowExecutionData;
	const executionStatus = executionData?.status ?? 'unknown';
	const errorNodeName = executionData?.data?.resultData.lastNodeExecuted;
	const errorNodeType = errorNodeName
		? workflowDocumentStore.value?.getNodeByName(errorNodeName)?.type
		: undefined;

	if (!executionData) {
		await builderStore.sendChatMessage({
			text: i18n.baseText('aiAssistant.builder.executeMessage.noExecutionData'),
			type: 'execution',
			executionStatus: 'error',
			errorMessage: 'Workflow execution data missing after run attempt.',
		});
		return;
	}

	if (executionStatus === 'success') {
		// Skip sending to AI when re-executing with test data still applied —
		// the first mock data execution already informed the AI, and re-executions
		// are just confirmations that don't need AI responses or workflow modifications.
		if (builderStore.pinDataApplied) {
			return;
		}

		await builderStore.sendChatMessage({
			text: i18n.baseText('aiAssistant.builder.executeMessage.executionSuccess'),
			type: 'execution',
			executionStatus,
		});
		return;
	}

	const executionError = executionData.data?.resultData.error?.message ?? 'Unknown error';
	const scopedErrorMessage = errorNodeName
		? i18n.baseText('aiAssistant.builder.executeMessage.executionFailedOnNode', {
				interpolate: {
					nodeName: errorNodeName,
					errorMessage: executionError,
				},
			})
		: i18n.baseText('aiAssistant.builder.executeMessage.executionFailed', {
				interpolate: { errorMessage: executionError },
			});

	const failureStatus = executionStatus === 'unknown' ? 'error' : executionStatus;

	await builderStore.sendChatMessage({
		text: scopedErrorMessage,
		type: 'execution',
		errorMessage: executionError,
		errorNodeType,
		executionStatus: failureStatus,
	});
}

async function onExecuteWithMockData() {
	builderStore.applyGeneratedPinData();

	const triggerNode = (workflowDocumentStore.value?.allNodes ?? []).find((node) =>
		nodeTypesStore.isTriggerNode(node.type),
	);

	// Chat trigger nodes require the user to send a message via the logs panel
	if (triggerNode && isChatNode(triggerNode)) {
		toast.showMessage({
			title: i18n.baseText('aiAssistant.builder.toast.title'),
			message: i18n.baseText('aiAssistant.builder.toast.description'),
			type: 'info',
		});
		logsStore.toggleOpen(true);
		return;
	}

	mockDataExecutionWatcherStop = watchExecutionCompletion(() => {
		mockDataExecutionWatcherStop = undefined;
		void onWorkflowExecuted();
	});

	await runWorkflow({
		triggerNode: workflowsStore.selectedTriggerNodeName ?? triggerNode?.name,
	});
}

function parseWorkflowJson(workflowJson: string): WorkflowDataUpdate | undefined {
	try {
		return jsonParse<WorkflowDataUpdate>(workflowJson);
	} catch (error) {
		handleError(error, {
			context: 'workflow-json-parse',
			title: i18n.baseText('aiAssistant.builder.workflowParsingError.title'),
			message: i18n.baseText('aiAssistant.builder.workflowParsingError.content'),
		});
		return undefined;
	}
}

// Watch for workflow updates and apply them
watch(
	() => builderStore.workflowMessages,
	async (messages) => {
		for (const msg of messages) {
			if (!msg.id || processedWorkflowUpdates.value.has(msg.id)) continue;
			if (!isWorkflowUpdatedMessage(msg)) continue;

			processedWorkflowUpdates.value.add(msg.id);

			const workflowData = parseWorkflowJson(msg.codeSnippet);
			if (!workflowData) continue;

			const result = await updateWorkflow(workflowData, {
				isInitialGeneration: builderStore.initialGeneration,
				nodeIdsToTidyUp: accumulatedNodeIdsToTidyUp.value,
			});

			if (!result.success) {
				handleError(result.error, { context: 'workflow-update' });
				builderStore.abortStreaming();
				return;
			}

			// Accumulate new node IDs so subsequent messages tidy up all new nodes
			if (result.newNodeIds.length > 0) {
				accumulatedNodeIdsToTidyUp.value = [
					...accumulatedNodeIdsToTidyUp.value,
					...result.newNodeIds,
				];
			}
		}
	},
	{ deep: true },
);

// Manage undo recording based on streaming state - start when streaming begins, stop when it ends
// This ensures all builder changes during a streaming session can be undone at once
watch(
	() => builderStore.streaming,
	(isStreaming, wasStreaming) => {
		if (isStreaming && !wasStreaming) {
			historyStore.startRecordingUndo();
		} else if (!isStreaming && wasStreaming) {
			historyStore.stopRecordingUndo();
		}
	},
);

// Reset initial generation flag and fit canvas when streaming ends
// Note: Saving is handled by auto-save in NodeView.vue
watch(
	() => builderStore.streaming,
	async (isStreaming, wasStreaming) => {
		// Only process when streaming just ended (was streaming, now not)
		if (!wasStreaming || isStreaming) {
			return;
		}

		if (
			builderStore.initialGeneration &&
			(workflowDocumentStore.value?.allNodes ?? []).length > 0
		) {
			builderStore.initialGeneration = false;
		}

		// Tidy up all nodes and zoom to fit after generation completes
		if (accumulatedNodeIdsToTidyUp.value.length > 0) {
			accumulatedNodeIdsToTidyUp.value = [];
			await nextTick();
			canvasEventBus.emit('tidyUp', {
				source: 'builder-update',
				trackEvents: false,
				trackHistory: false,
				trackBulk: false,
			});
			await nextTick();
			canvasEventBus.emit('fitView');
		}
	},
);

/**
 * Handle restore confirmation from a version card
 */
async function onRestoreConfirm(versionId: string, versionCardId: string) {
	try {
		const updatedWorkflow = await builderStore.restoreToVersion(versionId, versionCardId);
		if (!updatedWorkflow) {
			return;
		}

		processedWorkflowUpdates.value.clear();
		accumulatedNodeIdsToTidyUp.value = [];

		builderStore.clearExistingWorkflow();
		// Reload the workflow to reflect the restored state
		nodeViewEventBus.emit('importWorkflowData', {
			data: updatedWorkflow,
			tidyUp: false,
			regenerateIds: false,
			trackEvents: false,
			setStateDirty: false,
		});

		await nextTick();
		builderStore.builderMode = 'build';
	} catch (e: unknown) {
		toast.showMessage({
			type: 'error',
			title: i18n.baseText('aiAssistant.builder.restoreError.title'),
			message: e instanceof Error ? e.message : 'Unknown error',
		});
	}
}

/**
 * Handle "Show version" click - opens workflow history in a new tab
 */
function onShowVersion(versionId: string) {
	const route = router.resolve({
		name: VIEWS.WORKFLOW_HISTORY,
		params: {
			workflowId: workflowId.value,
			versionId,
		},
	});
	window.open(route.href, '_blank');
}

// Reset on route change, but not if streaming is in progress
watch(currentRoute, () => {
	if (!builderStore.streaming) {
		onNewWorkflow();
	}
});

// Refocus chat input when a canvas node is clicked while chat is open
watch(
	() => chatPanelStateStore.focusRequested,
	() => {
		void nextTick(() => {
			suggestionsInputRef.value?.focusInput() ??
				chatInputRef.value?.focusInput() ??
				n8nChatRef.value?.focusInput();
		});
	},
);

defineExpose({
	focusInput: () => {
		suggestionsInputRef.value?.focusInput() ??
			chatInputRef.value?.focusInput() ??
			n8nChatRef.value?.focusInput();
	},
});
</script>

<template>
	<div data-test-id="ask-assistant-chat" tabindex="0" :class="$style.container" @keydown.stop>
		<N8nAskAssistantChat
			ref="n8nChatRef"
			:user="user"
			:messages="displayMessages"
			:streaming="builderStore.streaming"
			:loading-message="loadingMessage"
			:thinking-completion-message="thinkingCompletionMessage"
			:mode="i18n.baseText('aiAssistant.builder.mode')"
			:show-stop="true"
			:scroll-on-new-message="true"
			:credits-quota="creditsQuota"
			:credits-remaining="creditsRemaining"
			:show-ask-owner-tooltip="showAskOwnerTooltip"
			:suggestions="workflowSuggestions"
			:input-placeholder="i18n.baseText('aiAssistant.builder.assistantPlaceholder')"
			:workflow-id="workflowId"
			:disabled="isChatInputDisabled"
			:disabled-tooltip="disabledTooltip"
			@close="emit('close')"
			@message="onUserMessage"
			@upgrade-click="() => goToUpgrade('ai-builder-sidebar', 'upgrade-builder')"
			@feedback="onFeedback"
			@stop="builderStore.abortStreaming"
			@code-replace="onCodeReplace"
			@code-undo="onCodeUndo"
		>
			<template #focused-nodes-chips="{ message }">
				<MessageFocusedNodesChips :focused-node-names="message.focusedNodeNames" />
			</template>
			<template v-if="slots.header || showUsabilityNotice" #header>
				<slot name="header" />
				<N8nInfoTip v-if="showUsabilityNotice" theme="warning" type="tooltip">
					<span>{{ i18n.baseText('aiAssistant.reducedHelp.chat.notice') }}</span>
				</N8nInfoTip>
			</template>
			<template v-if="creditsQuota !== undefined && creditsRemaining !== undefined" #headerActions>
				<CreditsSettingsDropdown
					:credits-remaining="creditsRemaining"
					:credits-quota="creditsQuota"
					:is-low-credits="builderStore.isLowCredits"
					@upgrade-click="() => goToUpgrade('ai-builder-sidebar', 'upgrade-builder')"
				/>
			</template>
			<template #inputHeader>
				<ReviewChangesBanner
					v-if="showReviewChanges"
					:node-changes="nodeChanges"
					:expanded="isExpanded"
					@toggle="toggleExpanded"
					@open-diff="openDiffView"
					@select-node="onSelectChangedNode"
				/>
				<CreditWarningBanner
					v-else-if="showCreditBanner"
					:credits-remaining="creditsRemaining"
					:credits-quota="creditsQuota"
					@upgrade-click="() => goToUpgrade('ai-builder-sidebar', 'upgrade-builder')"
					@dismiss="creditBannerDismissed = true"
				/>
				<Transition v-else name="slide">
					<NotificationPermissionBanner
						v-if="shouldShowNotificationBanner && !activeQuestionsMessage"
					/>
				</Transition>
			</template>
			<template #messagesFooter>
				<ExecuteMessage
					v-if="showExecuteMessage"
					@workflow-executed="onWorkflowExecuted"
					@execute-with-mock-data="onExecuteWithMockData"
				/>
			</template>
			<template #placeholder>
				<BuildModeEmptyState />
			</template>
			<template #custom-message="{ message }">
				<CollapsedMessagesGroup
					v-if="isCollapsedGroupMessage(message)"
					:messages="message.data.collapsedMessages"
					:version-node-changes-map="versionNodeChangesMap"
					:get-version-index="getVersionIndex"
					@open-diff="(versionId) => openDiffView(versionId)"
					@restore="onRestoreConfirm"
					@show-in-history="onShowVersion"
					@select-node="onSelectChangedNode"
				/>
				<ChatVersionCard
					v-else-if="isVersionCardMessage(message) && isNonEmptyVersionCard(message)"
					:version-id="message.data.versionId"
					:is-current="isCurrentVersionCard(message)"
					:node-changes="versionNodeChangesMap.get(message.data.versionId) ?? []"
					:prune-time-hours="workflowHistoryStore.evaluatedPruneTime"
					:title="message.data.title"
					:version-index="getVersionIndex(message)"
					:version-exists="!!message.data.createdAt"
					@open-diff="(versionId) => openDiffView(versionId)"
					@restore="(versionId) => onRestoreConfirm(versionId, message.id!)"
					@show-in-history="onShowVersion"
					@select-node="onSelectChangedNode"
				/>
				<!-- Always render questions message; when answered, collapse to intro text only -->
				<PlanQuestionsMessage
					v-if="isPlanModeQuestionsMessage(message)"
					:questions="message.data.questions"
					:intro-message="message.data.introMessage"
					:disabled="true"
					:answered="true"
				/>
				<PlanDisplayMessage
					v-else-if="isPlanModePlanMessage(message)"
					:message="message"
					:disabled="builderStore.streaming"
					:show-actions="isLastPlanMessage(message)"
					@decision="builderStore.resumeWithPlanDecision"
				/>
				<UserAnswersMessage
					v-else-if="isPlanModeUserAnswersMessage(message)"
					:answers="message.data.answers"
				/>
				<WebFetchApprovalMessage
					v-else-if="isWebFetchApprovalCustomMessage(message)"
					:data="message.data"
					:disabled="builderStore.streaming"
					@decision="onWebFetchDecision"
				/>
			</template>
			<template
				#suggestions-input="{
					modelValue,
					onUpdateModelValue,
					placeholder,
					disabled: slotDisabled,
					disabledTooltip: slotDisabledTooltip,
					streaming: slotStreaming,
					creditsQuota: slotCreditsQuota,
					creditsRemaining: slotCreditsRemaining,
					showAskOwnerTooltip: slotShowAskOwnerTooltip,
					onSubmit,
					onStop,
					onUpgradeClick,
					registerFocus,
				}"
			>
				<ChatInputWithMention
					ref="suggestionsInputRef"
					:model-value="modelValue"
					:placeholder="placeholder"
					:disabled="slotDisabled"
					:disabled-tooltip="slotDisabledTooltip"
					:streaming="slotStreaming"
					:credits-quota="slotCreditsQuota"
					:credits-remaining="slotCreditsRemaining"
					:show-ask-owner-tooltip="slotShowAskOwnerTooltip"
					@update:model-value="onUpdateModelValue"
					@submit="onSubmit"
					@stop="onStop"
					@upgrade-click="onUpgradeClick"
					@vue:mounted="registerFocus(() => suggestionsInputRef?.focusInput())"
				>
					<template v-if="builderStore.isPlanModeAvailable" #extra-actions>
						<PlanModeSelector
							:model-value="builderStore.builderMode"
							@update:model-value="builderStore.setBuilderMode"
						/>
					</template>
				</ChatInputWithMention>
			</template>
			<template #inputPlaceholder>
				<PlanQuestionsMessage
					v-if="activeQuestionsMessage"
					:questions="activeQuestionsMessage.data.questions"
					:disabled="builderStore.streaming"
					:answered="false"
					@submit="builderStore.resumeWithQuestionsAnswers"
					@telemetry="
						(event, properties) =>
							builderStore.trackWorkflowBuilderJourney(
								event as WorkflowBuilderJourneyEventType,
								properties,
							)
					"
				/>
				<ChatInputWithMention
					v-else
					ref="chatInputRef"
					v-model="inputText"
					:placeholder="i18n.baseText('aiAssistant.builder.assistantPlaceholder')"
					:disabled="isInputDisabled"
					:disabled-tooltip="disabledTooltip"
					:streaming="builderStore.streaming"
					:credits-quota="creditsQuota"
					:credits-remaining="creditsRemaining"
					:show-ask-owner-tooltip="showAskOwnerTooltip"
					@submit="onCustomInputSubmit"
					@stop="builderStore.abortStreaming"
					@upgrade-click="() => goToUpgrade('ai-builder-sidebar', 'upgrade-builder')"
				>
					<template v-if="builderStore.isPlanModeAvailable" #extra-actions>
						<PlanModeSelector
							:model-value="builderStore.builderMode"
							@update:model-value="builderStore.setBuilderMode"
						/>
					</template>
				</ChatInputWithMention>
			</template>
		</N8nAskAssistantChat>
	</div>
</template>

<style lang="scss" scoped>
.slide-enter-active,
.slide-leave-active {
	transition: transform var(--animation--duration) var(--animation--easing);
}

.slide-enter-from,
.slide-leave-to {
	transform: translateY(8px);
}
</style>

<style lang="scss" module>
.container {
	height: 100%;
	width: 100%;
}

.newWorkflowButtonWrapper {
	display: flex;
	flex-direction: column;
	flex-flow: wrap;
	gap: var(--spacing--2xs);
	background-color: var(--color--background--light-2);
	padding: var(--spacing--xs);
	border: 0;
}

.newWorkflowText {
	color: var(--color--text);
	font-size: var(--font-size--2xs);
}
</style>
